import { canonicalClinicalCode, clinicalCodeCategory, clinicalCodeChapter, isCategoryInRange } from "./normalise";

/**
 * Chapter-O categories that classify a *site* rather than a procedure. Chapter O is OPCS-4's
 * overflow chapter, so unlike chapter Z it holds both kinds and has to be enumerated.
 */
export const OPCS4_SITE_CODE_CATEGORIES: ReadonlySet<string> = new Set([
  "O11",
  "O12",
  "O13",
  "O14",
  "O16",
  "O28",
  "O30",
  "O31",
  "O33",
  "O34",
  "O36",
  "O42",
  "O43",
  "O45",
  "O46",
  "O47",
  "O50",
  "O52",
  "O53",
]);

/** Chapter Z is entirely anatomical sites; chapter O contributes the categories enumerated above. */
export const isOpcs4SiteCode = (code: string): boolean =>
  clinicalCodeChapter(code) === "Z" || OPCS4_SITE_CODE_CATEGORIES.has(clinicalCodeCategory(code));

/** Chapter Y — methods of operation (approach, laterality, laser, tattooing), qualifying the code before it. */
export const isOpcs4MethodCode = (code: string): boolean => clinicalCodeChapter(code) === "Y";

/**
 * A code that only ever qualifies another procedure, so it cannot stand in the primary position.
 * Per PGCS/PChSY1: chapter Y methods and chapter Z/O site codes are both subsidiary.
 */
export const isOpcs4SubsidiaryCode = (code: string): boolean => isOpcs4MethodCode(code) || isOpcs4SiteCode(code);

/** The complement of {@link isOpcs4SubsidiaryCode} — a code that can carry the primary procedure. */
export const isOpcs4PrimaryProcedureCode = (code: string): boolean => !isOpcs4SubsidiaryCode(code);

/**
 * `Y17.1` — use of diathermy. Its presence is what separates a "hot" snare or resection from the
 * otherwise identical cold one, so an ordering table lists the pair as adjacent entries.
 */
export const DIATHERMY_OPCS4_CODE = "Y17.1";

export const isDiathermyOpcs4Code = (code: string): boolean => canonicalClinicalCode(code) === DIATHERMY_OPCS4_CODE;

/** `Y39.5` — tattooing of organ. Supplementary: it never makes an episode therapeutic on its own. */
export const TATTOO_OPCS4_CODE = "Y39.5";

export const isTattooOpcs4Code = (code: string): boolean => canonicalClinicalCode(code) === TATTOO_OPCS4_CODE;

/** `Y20.3` (biopsy of lesion) and `Y20.9` (biopsy NEC) — the chapter-Y codes that record a biopsy. */
const BIOPSY_METHOD_OPCS4_CODES: ReadonlySet<string> = new Set(["Y20.3", "Y20.9"]);

export const isBiopsyMethodOpcs4Code = (code: string): boolean =>
  BIOPSY_METHOD_OPCS4_CODES.has(canonicalClinicalCode(code));

/**
 * Chapter-Y categories OPCS-4 names "Approach to organ ..." — how the organ was reached, as
 * opposed to what was done to it. Y51 and Y85 are the two an endoscopy uses.
 */
export const OPCS4_APPROACH_CODE_CATEGORIES: ReadonlySet<string> = new Set([
  "Y45",
  "Y46",
  "Y47",
  "Y48",
  "Y49",
  "Y50",
  "Y51",
  "Y52",
  "Y53",
  "Y68",
  "Y74",
  "Y75",
  "Y76",
  "Y77",
  "Y78",
  "Y79",
  "Y85",
]);

export const isOpcs4ApproachCode = (code: string): boolean =>
  OPCS4_APPROACH_CODE_CATEGORIES.has(clinicalCodeCategory(code));

/** `Y44.6` — application of clip. Supplementary, and like a tattoo it belongs to a site. */
export const CLIP_OPCS4_CODE = "Y44.6";

export const isClipOpcs4Code = (code: string): boolean => canonicalClinicalCode(code) === CLIP_OPCS4_CODE;

/** Chapter-Z categories an endoscopy can name: Z24–Z31 (airway to abdominal organs), Z61 (lymph node), Z94 (laterality). */
const ENDOSCOPY_Z_SITE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [24, 31],
  [61, 61],
  [94, 94],
];

/** Chapter-O site codes are never out of range. */
export const isOutOfEndoscopyRangeSiteCode = (code: string): boolean =>
  clinicalCodeChapter(code) === "Z" &&
  !ENDOSCOPY_Z_SITE_RANGES.some(([min, max]) => isCategoryInRange(code, "Z", min, max));

export const outOfEndoscopyRangeSiteCodeMessage = (code: string, description: string) =>
  `Site code outside the endoscopy range: ${code} (${description}). Check the site.`;
